/**
 * screens/CallLogScreen.tsx
 * Call Log — header + 4 KPIs + search/filter + horizontally-scrollable table.
 * UI recreated verbatim from the design HTML (lines ~274-341), max-width 1240.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, Pressable, ScrollView, TextInput } from 'react-native';
import { useTheme } from '../context/themeContext';
import { useConsole } from '../context/voiceConsoleContext';
import Icon from '../components/icon';
import { Card, Button, ScreenBody, outcomeColor } from '../components/voiceUi';
import { CallRow } from '../data/voiceMock';
import { listCallsPaged, getCallLogKpis } from '../services/voice/voiceService';
import { fmtDur, inr } from '../utils/format';

const PAGE_SIZE = 25;
// FE filter key → backend status enum. 'caller_dropped' has no dedicated status
// (it's disposition-derived) → map to FAILED, the closest enum bucket.
const STATUS_MAP: Record<Exclude<FilterKey, 'all'>, string> = {
  completed: 'COMPLETED',
  missed: 'MISSED',
  caller_dropped: 'FAILED',
};

// KPI-strip shape, derived from the service fn so we don't import the mock value.
type KpiData = Awaited<ReturnType<typeof getCallLogKpis>>['data'];
// Zero-seed so the strip paints its structure before the first load resolves.
const KPI_SEED: KpiData = { total: 0, completedPct: 0, completedN: 0, offered: 0, offeredAttempts: 0, accepted: 0, hitPct: 0, acceptedN: 0 };

// Table column template — matches the HTML grid-template-columns exactly.
const COLS = [1.4, 130, 90, 150, 1.2, 200, 90, 30];
const HEADERS = ['Contact', 'Direction', 'Duration', 'Outcome', 'Products discussed', 'Upsell', 'When', ''];

type FilterKey = 'all' | 'completed' | 'missed' | 'caller_dropped';
const FILTERS: { key: FilterKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'completed', label: 'Completed' },
  { key: 'missed', label: 'Missed' },
  { key: 'caller_dropped', label: 'Caller dropped' },
];

function outLabel(o: string): string {
  if (o === 'completed') return 'Completed';
  if (o === 'missed') return 'Missed';
  return 'Caller dropped';
}

// Filled outcome pill with a dot, as rendered in the HTML row.
function OutcomePill({ outcome }: { outcome: CallRow['outcome'] }) {
  const c = useTheme().theme.colors; const t = useTheme().theme;
  const dot = { width: 7, height: 7, borderRadius: 999, backgroundColor: outcomeColor(outcome, c) };
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, height: 24, paddingHorizontal: 11, borderWidth: 1, borderColor: c.line, borderRadius: 20, backgroundColor: c.paper2, alignSelf: 'flex-start' }}>
      <View style={dot} />
      <Text style={{ fontSize: 11.5, color: c.ink, fontFamily: t.font.sans }}>{outLabel(outcome)}</Text>
    </View>
  );
}

// Direction pill with the matching arrow icon (inbound/outbound).
function DirPill({ dir }: { dir: CallRow['dir'] }) {
  const c = useTheme().theme.colors; const t = useTheme().theme;
  return (
    <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, height: 24, paddingHorizontal: 10, borderWidth: 1, borderColor: c.line, borderRadius: 20, alignSelf: 'flex-start' }}>
      <Icon name={dir === 'inbound' ? 'arrow-d' : 'arrow-u'} size={12} color={c.ink2} />
      <Text style={{ fontSize: 11.5, color: c.ink2, fontFamily: t.font.sans }}>{dir}</Text>
    </View>
  );
}

export default function CallLogScreen() {
  const { theme } = useTheme();
  const c = theme.colors;
  const { openCall, orgId } = useConsole();
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [filter, setFilter] = useState<FilterKey>('all');
  // Direction facet (SKVOICE-OUTBOUND §3): All / Inbound / Outbound.
  const [dirFilter, setDirFilter] = useState<'all' | 'inbound' | 'outbound'>('all');
  const [page, setPage] = useState(1);

  // Server-side page: filter (caller/status) + paging happen in the BACKEND, so
  // search scans ALL calls, not the loaded slice. KPI strip is a separate rollup.
  const [rows, setRows] = useState<CallRow[]>([]);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [kpiData, setKpiData] = useState<KpiData>(KPI_SEED);
  const [live, setLive] = useState(true);
  const [loading, setLoading] = useState(false);

  // Debounce the search box so we don't fire a request per keystroke; a new query
  // resets to page 1.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search.trim()); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  // Reset to page 1 whenever the status filter or org changes.
  useEffect(() => { setPage(1); }, [filter, orgId]);

  const loadPage = useCallback(async () => {
    setLoading(true);
    const page$ = await listCallsPaged({
      page,
      pageSize: PAGE_SIZE,
      ...(debouncedSearch ? { caller: debouncedSearch } : {}),
      ...(filter !== 'all' ? { status: STATUS_MAP[filter] } : {}),
    });
    setRows(page$.data.data);
    setTotal(page$.data.total);
    setTotalPages(page$.data.totalPages);
    setLive(page$.live);
    setLoading(false);
  }, [page, debouncedSearch, filter]);

  // KPI strip is org-scoped and independent of paging/search.
  const loadKpis = useCallback(async () => {
    const kpi = await getCallLogKpis();
    setKpiData(kpi.data);
  }, []);

  useEffect(() => { void loadPage(); }, [loadPage]);
  useEffect(() => { void loadKpis(); }, [loadKpis, orgId]);

  const kpis: { label: string; value: string; sub: string }[] = [
    { label: 'Calls', value: String(kpiData.total), sub: `${kpiData.total} total` },
    { label: 'Completed', value: `${kpiData.completedPct}%`, sub: `${kpiData.completedN} of ${kpiData.total}` },
    { label: 'Upsell offered', value: inr(kpiData.offered), sub: `${kpiData.offeredAttempts} attempts` },
    { label: 'Upsell accepted', value: inr(kpiData.accepted), sub: `${kpiData.hitPct}% hit · ${kpiData.acceptedN} accepted` },
  ];

  // Apply the direction facet client-side (outcome filter is server-side).
  const dirRows = dirFilter === 'all' ? rows : rows.filter((r) => r.dir === dirFilter);

  return (
    <ScreenBody maxWidth={1240}>
      {/* Header */}
      <View style={{ flexDirection: 'row', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, marginBottom: 18 }}>
        <View style={{ flex: 1 }}>
          <Text style={{ fontSize: 22, fontWeight: '600', letterSpacing: -0.44, color: c.ink, fontFamily: theme.font.sans }}>Calls</Text>
          <Text style={{ fontSize: 12.5, color: c.ink3, marginTop: 2, fontFamily: theme.font.sans }}>All SK Voice-handled calls. Tap a row for transcript + summary.</Text>
        </View>
        <View style={{ flexDirection: 'row', gap: 9 }}>
          <Button kind="default" label="Export" icon="download" />
          <Button kind="primary" label="Start outbound call" icon="phone" />
        </View>
      </View>

      {/* 4-col KPI grid */}
      <View style={{ flexDirection: 'row', gap: 12, marginBottom: 16 }}>
        {kpis.map(k => (
          <View key={k.label} style={{ flex: 1 }}>
            <Card style={{ padding: 15, paddingVertical: 14 }}>
              <Text style={{ fontSize: 10.5, fontWeight: '600', letterSpacing: 0.63, textTransform: 'uppercase', color: c.ink4, marginBottom: 8, fontFamily: theme.font.sans }}>{k.label}</Text>
              <Text style={{ fontSize: 24, fontWeight: '600', letterSpacing: -0.48, lineHeight: 24, color: c.ink, fontFamily: theme.font.sans }}>{k.value}</Text>
              <Text style={{ fontSize: 11, color: c.ink4, marginTop: 6, fontFamily: theme.font.sans }}>{k.sub}</Text>
            </Card>
          </View>
        ))}
      </View>

      {/* Filter row */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <View style={{ flex: 1, minWidth: 220, maxWidth: 340, justifyContent: 'center' }}>
          <View style={{ position: 'absolute', left: 11, zIndex: 1 }}>
            <Icon name="search" size={15} color={c.ink4} />
          </View>
          <TextInput
            value={search}
            onChangeText={setSearch}
            placeholder="Search by phone or contact…"
            placeholderTextColor={c.ink4}
            style={{ width: '100%', height: 36, paddingLeft: 32, paddingRight: 12, borderWidth: 1, borderColor: c.lineStrong, borderRadius: 8, backgroundColor: c.card, color: c.ink, fontSize: 12.5, fontFamily: theme.font.sans } as any}
          />
        </View>
        {/* Direction facet — All · Inbound · Outbound */}
        {(['all', 'inbound', 'outbound'] as const).map((d) => {
          const active = dirFilter === d;
          return (
            <Pressable key={d} onPress={() => setDirFilter(d)}
              style={{ height: 28, paddingHorizontal: 12, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? c.ink : c.card, borderWidth: active ? 0 : 1, borderColor: c.line }}>
              <Text style={{ fontSize: 12, fontWeight: '500', color: active ? c.paper : c.ink2, fontFamily: theme.font.sans }}>{d === 'all' ? 'All' : d === 'inbound' ? 'Inbound' : 'Outbound'}</Text>
            </Pressable>
          );
        })}
        <View style={{ width: 1, height: 20, backgroundColor: c.line, marginHorizontal: 2 }} />
        {FILTERS.map(f => {
          const active = filter === f.key;
          return (
            <Pressable key={f.key} onPress={() => setFilter(f.key)}
              style={{ height: 28, paddingHorizontal: 12, borderRadius: 20, alignItems: 'center', justifyContent: 'center', backgroundColor: active ? c.ink : c.card, borderWidth: active ? 0 : 1, borderColor: c.line }}>
              <Text style={{ fontSize: 12, fontWeight: '500', color: active ? c.paper : c.ink2, fontFamily: theme.font.sans }}>{f.label}</Text>
            </Pressable>
          );
        })}
        <View style={{ flex: 1 }} />
        {!live && (
          <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.mono }}>Couldn’t load — check connection</Text>
        )}
        <Text style={{ fontSize: 12, color: c.ink4, fontFamily: theme.font.sans }}>{total} calls</Text>
      </View>

      {/* Table */}
      <Card style={{ overflow: 'hidden' }}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={{ flexGrow: 1 }}>
          {/* flexGrow: fill the card when it's wider than 1000; minWidth: still scroll when narrower. */}
          <View style={{ flexGrow: 1, minWidth: 1000 }}>
            {/* header row */}
            <View style={{ flexDirection: 'row', gap: 14, paddingHorizontal: 18, paddingVertical: 11, borderBottomWidth: 1, borderBottomColor: c.line, backgroundColor: c.paper2 }}>
              {HEADERS.map((h, i) => (
                <View key={i} style={COLS[i] < 10 ? { flex: COLS[i] } : { width: COLS[i] }}>
                  <Text style={{ fontSize: 10.5, fontWeight: '600', letterSpacing: 0.52, textTransform: 'uppercase', color: c.ink4, fontFamily: theme.font.sans }}>{h}</Text>
                </View>
              ))}
            </View>
            {/* empty state — no rows match (or none loaded yet) */}
            {dirRows.length === 0 && (
              <View style={{ minWidth: 1000, paddingHorizontal: 18, paddingVertical: 40, alignItems: 'center' }}>
                <Text style={{ fontSize: 12.5, color: c.ink4, fontFamily: theme.font.sans }}>No calls to show.</Text>
              </View>
            )}
            {/* body rows */}
            {dirRows.map((r, ri) => (
              <Pressable key={r.id} onPress={() => openCall(r.id, { id: r.id, contact: r.contact, dir: r.dir, outcome: r.outcome, durSec: r.durSec })}
                style={{ flexDirection: 'row', gap: 14, alignItems: 'center', minHeight: 64, paddingHorizontal: 18, paddingVertical: 12, borderBottomWidth: ri === dirRows.length - 1 ? 0 : 1, borderBottomColor: c.line }}>
                {/* Contact */}
                <View style={{ flex: COLS[0], flexDirection: 'row', alignItems: 'center', gap: 11 }}>
                  <View style={{ width: 32, height: 32, borderRadius: 999, backgroundColor: c.paper3, borderWidth: 1, borderColor: c.line, alignItems: 'center', justifyContent: 'center' }}>
                    <Text style={{ fontSize: 11, fontWeight: '600', color: c.ink2, fontFamily: theme.font.sans }}>{r.initials}</Text>
                  </View>
                  <View style={{ flex: 1 }}>
                    <Text numberOfLines={1} style={{ fontWeight: '600', fontSize: 13, color: c.ink, fontFamily: theme.font.sans }}>{r.contact}</Text>
                    <Text style={{ fontFamily: theme.font.mono, fontSize: 10.5, color: c.ink4 }}>{r.id}</Text>
                  </View>
                </View>
                {/* Direction */}
                <View style={{ width: COLS[1] }}><DirPill dir={r.dir} /></View>
                {/* Duration */}
                <View style={{ width: COLS[2] }}>
                  <Text style={{ fontFamily: theme.font.mono, fontSize: 12.5, color: r.dur ? c.ink2 : c.ink4 }}>{r.dur ? r.dur : '—'}</Text>
                </View>
                {/* Outcome */}
                <View style={{ width: COLS[3] }}><OutcomePill outcome={r.outcome} /></View>
                {/* Products */}
                <View style={{ flex: COLS[4] }}>
                  <Text numberOfLines={1} style={{ fontSize: 12.5, color: c.ink2, fontFamily: theme.font.sans }}>{r.products.length ? r.products.join(', ') : '—'}</Text>
                </View>
                {/* Upsell */}
                <View style={{ width: COLS[5] }}>
                  {r.upsell ? (
                    <View style={{ gap: 5 }}>
                      <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
                        <Text style={{ fontFamily: theme.font.mono, fontSize: 11.5 }}>
                          <Text style={{ fontWeight: '600', color: c.ink }}>{inr(r.upsell.acc)}</Text>
                          <Text style={{ color: c.ink4 }}> / {inr(r.upsell.off)}</Text>
                        </Text>
                        <Text style={{ fontFamily: theme.font.mono, fontSize: 11, color: c.ink3 }}>{r.upsell.pct}%</Text>
                      </View>
                      <View style={{ height: 5, backgroundColor: c.paper3, borderRadius: 20, overflow: 'hidden' }}>
                        <View style={{ height: '100%', width: `${r.upsell.pct}%`, backgroundColor: c.ink, borderRadius: 20 }} />
                      </View>
                      <Text style={{ fontSize: 10.5, color: c.ink4, fontFamily: theme.font.sans }}>{r.upsell.nAcc} of {r.upsell.nOff} accepted</Text>
                    </View>
                  ) : (
                    <Text style={{ color: c.ink4, fontFamily: theme.font.sans }}>—</Text>
                  )}
                </View>
                {/* When */}
                <View style={{ width: COLS[6] }}>
                  <Text style={{ fontSize: 11.5, color: c.ink4, fontFamily: theme.font.sans }}>{r.when}</Text>
                </View>
                {/* chevron */}
                <View style={{ width: COLS[7], alignItems: 'flex-start' }}>
                  <Icon name="v-chev-r" size={15} color={c.ink4} style={{ opacity: 0.4 }} />
                </View>
              </Pressable>
            ))}
          </View>
        </ScrollView>
      </Card>

      {/* Numbered pagination — page window + prev/next + total, all server-side. */}
      {totalPages > 1 && (
        <View style={{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginTop: 14 }}>
          <Text style={{ fontSize: 12, color: c.ink4, fontFamily: theme.font.sans }}>
            Page {page} of {totalPages} · {total} calls{loading ? ' · loading…' : ''}
          </Text>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <PagerBtn label="‹ Prev" disabled={page <= 1 || loading} onPress={() => setPage(p => Math.max(1, p - 1))} c={c} t={theme} />
            {pageWindow(page, totalPages).map((p, i) =>
              p === -1 ? (
                <Text key={`gap${i}`} style={{ fontSize: 12, color: c.ink4, paddingHorizontal: 4, fontFamily: theme.font.mono }}>…</Text>
              ) : (
                <Pressable key={p} onPress={() => setPage(p)} disabled={loading}
                  style={{ minWidth: 30, height: 30, paddingHorizontal: 8, borderRadius: 8, alignItems: 'center', justifyContent: 'center', backgroundColor: p === page ? c.ink : c.card, borderWidth: p === page ? 0 : 1, borderColor: c.line }}>
                  <Text style={{ fontSize: 12, fontWeight: p === page ? '600' : '500', color: p === page ? c.paper : c.ink2, fontFamily: theme.font.mono }}>{p}</Text>
                </Pressable>
              ),
            )}
            <PagerBtn label="Next ›" disabled={page >= totalPages || loading} onPress={() => setPage(p => Math.min(totalPages, p + 1))} c={c} t={theme} />
          </View>
        </View>
      )}
    </ScreenBody>
  );
}

// A prev/next pager button (disabled state dims + blocks press).
function PagerBtn({ label, disabled, onPress, c, t }: { label: string; disabled: boolean; onPress: () => void; c: any; t: any }) {
  return (
    <Pressable onPress={onPress} disabled={disabled}
      style={{ height: 30, paddingHorizontal: 11, borderRadius: 8, alignItems: 'center', justifyContent: 'center', borderWidth: 1, borderColor: c.line, backgroundColor: c.card, opacity: disabled ? 0.4 : 1 }}>
      <Text style={{ fontSize: 12, fontWeight: '500', color: c.ink2, fontFamily: t.font.sans }}>{label}</Text>
    </Pressable>
  );
}

// Compact page window: first, last, current±1, with … gaps. Keeps the pager to a
// fixed width even at hundreds of pages.
function pageWindow(page: number, totalPages: number): number[] {
  const out = new Set<number>([1, totalPages, page, page - 1, page + 1]);
  const pages = [...out].filter(p => p >= 1 && p <= totalPages).sort((a, b) => a - b);
  const withGaps: number[] = [];
  for (let i = 0; i < pages.length; i++) {
    if (i > 0 && pages[i] - pages[i - 1] > 1) withGaps.push(-1); // … gap marker
    withGaps.push(pages[i]);
  }
  return withGaps;
}
